import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../contexts/AuthContext';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import LoginBackdrop from '../components/LoginBackdrop';

const REMEMBER_KEY = 'gastify_remember_superadmin_user';

export default function SuperAdminLogin() {
  const [username, setUsername] = useState('');
  const [senha, setSenha] = useState('');
  const [lembrar, setLembrar] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useSuperAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) { setUsername(saved); setLembrar(true); }
    } catch {}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !senha) { setError('Preencha todos os campos.'); return; }
    setLoading(true); setError('');
    try {
      await login(username, senha);
      try {
        if (lembrar) localStorage.setItem(REMEMBER_KEY, username);
        else localStorage.removeItem(REMEMBER_KEY);
      } catch {}
      navigate('/superadmin/dashboard');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Usuário ou senha inválidos');
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative bg-slate-900 overflow-hidden"
      data-testid="superadmin-login-page"
    >
      <LoginBackdrop image="/login-bg.jpg" />
      <div className="absolute inset-0 bg-slate-950/10 pointer-events-none" />
      <div className="w-full max-w-sm relative z-10">
        <div className="glass-card rounded-none p-8 page-enter">
          <div className="text-center mb-7">
            <img src="/gastify-mark.png" alt="Gastify" className="w-16 h-16 mx-auto mb-4 rounded-2xl shadow-lg border border-white/30" data-testid="sa-login-logo" />
            <h1 className="text-2xl font-bold text-white tracking-tight glass-text-shadow" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Gastify · Painel Master
            </h1>
            <p className="text-sm text-white/80 mt-1 glass-text-shadow">Gerenciamento global de tenants</p>
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-500/15 border border-red-400/30 rounded-2xl text-sm text-red-200" data-testid="sa-login-error">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                className="w-full px-5 py-3 rounded-full glass-input text-sm focus:outline-none transition-colors"
                placeholder="Usuário" data-testid="sa-username-input" disabled={loading} />
            </div>
            <div className="relative">
              <input type={showSenha ? 'text' : 'password'} value={senha} onChange={e => setSenha(e.target.value)}
                className="w-full px-5 py-3 pr-11 rounded-full glass-input text-sm focus:outline-none transition-colors"
                placeholder="Digite sua senha" data-testid="sa-senha-input" disabled={loading} />
              <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                onClick={() => setShowSenha(v => !v)}>
                {showSenha ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="flex items-center justify-between px-1 text-xs text-white/85">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={lembrar} onChange={e => setLembrar(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-white/40 bg-white/10 accent-white" />
                Lembrar usuário
              </label>
            </div>
            <button type="submit" disabled={loading}
              className="w-full justify-center py-3 mt-2 inline-flex items-center gap-2 glass-btn text-sm font-semibold rounded-full disabled:opacity-60"
              data-testid="sa-login-submit-btn">
              {loading ? 'Entrando...' : (<><LogIn size={16} /> Entrar</>)}
            </button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-200 mt-4 drop-shadow">
          Gastify · Cada gasto, uma decisão consciente · Akuant Solutions &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
